import React, { useState, useCallback } from "react";
import {
  DataTable,
  DataTableRowEvent,
  DataTableRowToggleEvent,
  DataTableSelectionMultipleChangeEvent,
} from "primereact/datatable";
import { Column, ColumnEditorOptions, ColumnEvent } from "primereact/column";
import { Paginator } from "primereact/paginator";

import type {
  ExpandableDataTableProps,
  ColumnDefinition,
  RowEditEvent,
} from "./types";

/**
 * Default loading skeleton component
 */
function DefaultLoadingSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-10 bg-gray-200 rounded animate-pulse"
          style={{ animationDelay: `${i * 100}ms` }}
        />
      ))}
    </div>
  );
}

/**
 * A generic, composable expandable data table built on PrimeReact DataTable.
 *
 * Supports:
 * - Nested row expansion with lazy loading
 * - Row selection (single, multiple, checkbox)
 * - Cell editing
 * - Pagination (server-side or client-side)
 * - Sorting
 * - Full TypeScript support
 *
 * @example
 * ```tsx
 * <ExpandableDataTable
 *   data={items}
 *   dataKey="id"
 *   columns={[
 *     { header: "Name", field: "name", width: "30%" },
 *     { header: "Cost", body: (r) => formatCurrency(r.cost) },
 *   ]}
 *   expandable
 *   onRowExpand={(row) => fetchChildren(row.id)}
 *   renderExpandedRow={(row) => <ChildTable data={childData[row.id]} />}
 * />
 * ```
 */
export default function ExpandableDataTable<T extends Record<string, unknown>>({
  // Data
  data,
  dataKey,
  columns,
  loading = false,
  loadingTemplate,
  emptyMessage = "No records found",

  // Expansion
  expandable = false,
  allowExpansion,
  onRowExpand,
  onRowCollapse,
  renderExpandedRow,
  expandedRows: controlledExpandedRows,
  onExpandedRowsChange,

  // Selection
  selectable = false,
  selectionMode = "checkbox",
  selectedItems,
  onSelectionChange,

  // Pagination
  paginated = false,
  paginationConfig,

  // Cell Editing
  editable = false,
  editMode = "cell",
  onRowEditComplete,

  // Styling
  tableClassName = "min-w-full bg-white divide-y divide-gray-300 text-sm",
  cellClassName = "p-2 border-b border-gray-200 text-sm",
  rowClassName,
  wrapperClassName = "",

  // Header Actions
  headerActions,
  footer,

  // Sorting
  sortable = false,
  sortField,
  sortOrder,
  onSort,

  // Additional PrimeReact props
  scrollable,
  scrollHeight,
  responsiveLayout = "scroll",
  size = "normal",
  showGridlines = false,
  stripedRows = false,
  resizableColumns = false,
  reorderableColumns = false,
}: ExpandableDataTableProps<T>) {
  // Internal expanded rows state (used when not controlled)
  const [internalExpandedRows, setInternalExpandedRows] = useState<
    Record<string, boolean>
  >({});

  // Use controlled or internal state
  const expandedRows = controlledExpandedRows ?? internalExpandedRows;

  // Handle row toggle - use ref to avoid stale closure issues
  const handleRowToggle = useCallback(
    (e: DataTableRowToggleEvent) => {
      const newExpandedRows = e.data as Record<string, boolean>;
      if (onExpandedRowsChange) {
        onExpandedRowsChange(newExpandedRows);
      } else {
        setInternalExpandedRows(newExpandedRows);
      }
    },
    [onExpandedRowsChange],
  );

  // Handle row expand
  const handleRowExpand = useCallback(
    (e: DataTableRowEvent) => {
      if (onRowExpand) {
        onRowExpand(e.data as T);
      }
    },
    [onRowExpand],
  );

  // Handle row collapse
  const handleRowCollapse = useCallback(
    (e: DataTableRowEvent) => {
      if (onRowCollapse) {
        onRowCollapse(e.data as T);
      }
    },
    [onRowCollapse],
  );

  // Row expansion template
  const rowExpansionTemplate = useCallback(
    (rowData: T) => {
      if (renderExpandedRow) {
        return renderExpandedRow(rowData);
      }
      return null;
    },
    [renderExpandedRow],
  );

  // Default allow expansion function
  const defaultAllowExpansion = useCallback(() => true, []);

  // Handle selection change
  const handleSelectionChange = useCallback(
    (e: DataTableSelectionMultipleChangeEvent<T[]>) => {
      if (onSelectionChange) {
        onSelectionChange(e.value as T[]);
      }
    },
    [onSelectionChange],
  );

  // Handle sort
  const handleSort = useCallback(
    (e: { sortField: string; sortOrder: 1 | -1 | 0 | null | undefined }) => {
      if (onSort && e.sortField) {
        onSort({
          sortField: e.sortField,
          sortOrder: (e.sortOrder ?? 1) as 1 | -1,
        });
      }
    },
    [onSort],
  );

  // Handle row edit complete
  const handleRowEditComplete = useCallback(
    (e: {
      originalEvent: React.SyntheticEvent;
      data: T;
      newData: T;
      index: number;
    }) => {
      if (onRowEditComplete) {
        onRowEditComplete({
          originalEvent: e.originalEvent,
          data: e.data,
          newData: e.newData,
          index: e.index,
        } as RowEditEvent<T>);
      }
    },
    [onRowEditComplete],
  );

  // Build column style from definition
  const getColumnStyle = (col: ColumnDefinition<T>): React.CSSProperties => {
    const style: React.CSSProperties = { ...col.style };
    if (col.width) {
      style.width = col.width;
    }
    if (col.align) {
      style.textAlign = col.align;
    }
    return style;
  };

  // Render loading state
  if (loading) {
    return (
      <div className={wrapperClassName}>
        {loadingTemplate ?? <DefaultLoadingSkeleton />}
      </div>
    );
  }

  // Build expansion props conditionally
  const expansionProps = expandable
    ? {
        expandedRows,
        onRowToggle: handleRowToggle,
        onRowExpand: handleRowExpand,
        onRowCollapse: handleRowCollapse,
        rowExpansionTemplate,
      }
    : {};

  // Build selection props conditionally
  const selectionProps = selectable
    ? {
        selection: selectedItems,
        onSelectionChange: handleSelectionChange as (
          e: DataTableSelectionMultipleChangeEvent<T[]>,
        ) => void,
        selectionMode: selectionMode as "multiple" | "checkbox",
      }
    : {};

  // Build sorting props conditionally
  const sortingProps = sortable
    ? {
        sortField,
        sortOrder,
        onSort: handleSort,
      }
    : {};

  return (
    <div className={wrapperClassName}>
      {/* Header Actions */}
      {headerActions && <div className="mb-4">{headerActions}</div>}

      {/* Main Table */}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <DataTable
        value={data as any}
        dataKey={dataKey as string}
        {...(expansionProps as any)}
        {...(selectionProps as any)}
        {...(sortingProps as any)}
        // Editing
        editMode={editable ? editMode : undefined}
        onRowEditComplete={
          editable && editMode === "row"
            ? (handleRowEditComplete as any)
            : undefined
        }
        // Styling
        tableClassName={tableClassName}
        cellClassName={cellClassName ? () => cellClassName : undefined}
        rowClassName={rowClassName as any}
        emptyMessage={emptyMessage}
        // Additional props
        scrollable={scrollable}
        scrollHeight={scrollHeight}
        responsiveLayout={responsiveLayout}
        size={size}
        showGridlines={showGridlines}
        stripedRows={stripedRows}
        resizableColumns={resizableColumns}
        reorderableColumns={reorderableColumns}
      >
        {/* Selection Column */}
        {selectable && selectionMode === "checkbox" && (
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
            frozen={columns.some((c) => c.frozen)}
          />
        )}

        {/* Data Columns */}
        {columns.map((col, idx) => (
          <Column
            key={col.field?.toString() ?? `col-${idx}`}
            field={col.field as string}
            header={col.header}
            body={
              col.body
                ? (rowData: T, options: { rowIndex: number }) =>
                    col.body!(rowData, options)
                : undefined
            }
            style={getColumnStyle(col)}
            headerStyle={col.headerStyle}
            className={col.className}
            sortable={sortable && col.sortable}
            editor={
              editable && col.editable && col.editor
                ? (options: ColumnEditorOptions) =>
                    col.editor!({
                      value: options.value,
                      field: options.field,
                      rowData: options.rowData as T,
                      rowIndex: options.rowIndex,
                      column: col,
                      editorCallback: (val: unknown) =>
                        options.editorCallback?.(val),
                    })
                : undefined
            }
            onCellEditComplete={
              editable && col.editable && col.onCellEditComplete
                ? (e: ColumnEvent) =>
                    col.onCellEditComplete!({
                      originalEvent: e.originalEvent,
                      value: (e as unknown as { value: unknown }).value,
                      newValue: (e as unknown as { newValue: unknown })
                        .newValue,
                      field: e.field,
                      rowData: e.rowData as T,
                      rowIndex: e.rowIndex,
                      column: col,
                    })
                : undefined
            }
            frozen={col.frozen}
          />
        ))}

        {/* Row Editor Column */}
        {editable && editMode === "row" && (
          <Column
            rowEditor
            headerStyle={{ width: "7rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
        )}

        {/* Expander Column */}
        {expandable && (
          <Column
            expander={allowExpansion ?? defaultAllowExpansion}
            style={{ width: "4rem" }}
          />
        )}
      </DataTable>

      {/* Pagination */}
      {paginated && paginationConfig && (
        <div className="mt-2">
          <Paginator
            first={paginationConfig.first}
            rows={paginationConfig.rows}
            totalRecords={paginationConfig.totalRecords}
            onPageChange={paginationConfig.onPageChange}
            rowsPerPageOptions={paginationConfig.rowsPerPageOptions}
            template={paginationConfig.template}
          />
        </div>
      )}

      {/* Footer */}
      {footer && <div className="mt-4">{footer}</div>}
    </div>
  );
}
